<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Add User
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="partner_id" label="Partner" required>
                        <USelectMenu
                            v-model="state.partner_id"
                            :options="partners"
                            value-attribute="id"
                            option-attribute="name"
                            size="xl"
                        />
                    </UFormGroup>
                    <UFormGroup name="name" label="Name" required>
                        <UInput v-model="state.name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="email" label="Email" required>
                        <UInput v-model="state.email" type="email" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="password" label="Password" required help="Share it with the user; they can change it after signing in.">
                        <UInput v-model="state.password" type="text" size="xl" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Partner } from '~/types/partner'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    partners: Pick<Partner, 'id' | 'name'>[]
    partnerId?: string
}>()

const toast = useToast()
const loading = ref<boolean>(false)
const state = ref<{ partner_id?: string; name?: string; email?: string; password?: string }>({})

const schema = z.object({
    partner_id: z.string().min(1, 'Required'),
    name: z.string().trim().min(2, 'Enter at least 2 characters'),
    email: z.string().trim().email('Enter a valid email'),
    password: z.string().min(8, 'Use at least 8 characters'),
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = { partner_id: props.partnerId ?? props.partners[0]?.id }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    try {
        await $fetch('/api/partner/users', { method: 'POST', body: event.data })
        toast.add({ title: 'User created', color: 'green' })
        dismiss(true)
    } catch (error: any) {
        toast.add({ title: 'Could not create user', description: error.data?.message, color: 'red' })
    }

    loading.value = false
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) {
        emit('reload', true)
    }
}
</script>
